/* @flow */


import { getRandom, generateUUID } from '../utils';
import bidbase, { INIT_STATUS } from './bidbase';
import { isFetchSupported, fetchJSON } from '../utils/fetch';
import {
  BrowserUtil,
  isJSONSupported,
  getViewportDimensions
} from '../utils/browserutil';
import { logError } from '../utils/logger';
import { extend, isEmptyObject, isNumber } from '../utils/coreutils';
import { getRTBUserEids } from '../idproviders/idputils';
import containerHelper from '../container-helper';
import { isDNT } from '../utils/privacyutils';
import adconfig from '../adconfig';

/**
 * @global
 * @typedef hbPosData
 * @property {string} info Position Info
 * @property {object} _QP Position Query Param
 * @property {object} _CV Position Custom Vars
 * @property {hb_data} winningBid Winning bid
 */

/**
 * typedef definitions
 */
declare type deviceExtInfo = {|
  ext: {
    vpw: number,
    vph: number
  }
|};

declare type deviceInfo = {|
  device: {
    w: number,
    h: number,
    dnt: number,
    ua: string,
    language: string,
    ...deviceExtInfo
  }
|};

declare type rtbUid = {
  id: string | Array<string>,
  ext?: {
    stype: string
  },
  atype: number
};

declare type rtbEid = {
  source: string,
  uids: Array<rtbUid>
};

declare type userInfo = {
  ext: {
    eids: Array<rtbEid>
  }
};

declare type regsInfo = {|
  regs: {
    ext: {
      us_privacy: string
    }
  }
|};

declare type siteContentInfo = {|
  language: string
|};

declare type siteExtInfo = {|
  data: {
    documentLang: string
  }
|};

declare type sitePublisherInfo = {|
  domain: string
|};

declare type siteInfo = {|
  site: {
    domain: string,
    page: string,
    content: siteContentInfo,
    ext: siteExtInfo,
    publisher: sitePublisherInfo
  }
|};

declare type sourceInfo = {|
  ext?: {
    schain?: anyObject
  }
|};

declare type ortb2Info = {|
  ...deviceInfo,
  ...regsInfo,
  ...siteInfo,
  user: userInfo,
  source?: sourceInfo
|};

declare type ortb2ImpInfo = {|
  ext: {
    data: {
      adserver: {
        adslot: string,
        name: string
      }
    },
    gpid: string
  }
|};

declare type mediaTypesInfo = {|
  banner: {
    floor: number,
    sizes: posSizes
  }
|};

declare type paramsInfo = {|
  siteId: number
|};

declare type bidInfo = {|
  adUnitCode: string,
  adUnitId: string,
  auctionId: string,
  auctionsCount: number,
  bidId: string,
  bidder: string,
  bidRequestsCount: number,
  bidderRequestId: string,
  bidderRequestsCount: number,
  bidderWinsCount: number,
  mediaTypes: mediaTypesInfo,
  ortb2: ortb2Info,
  ortb2Imp: ortb2ImpInfo,
  params: paramsInfo,
  sizes: posSizes,
  transactionId: string,
  deferBilling: boolean,
  src: string
|};

declare type refererInfo = {
  domain: string,
  legacy: {
    referer: string
  },
  location: string,
  page: string,
  topmostLocation: string
};

declare type unrulyReqBody = {
  bidderRequest: {
    auctionStart: number,
    auctionId: string,
    invalidBidsCount: number,
    bids: Array<bidInfo>,
    ortb2: ortb2Info,
    refererInfo: refererInfo,
    start: number,
    timeout: number,
    uspConsent: string
  }
};

declare type unrulyBidRequestCache = {
  impid: {
    [string]: string
  }
};

declare type resBid = {
  requestId: string,
  cpm: number,
  currency: string,
  width: number,
  height: number,
  creativeId: string,
  meta: {
    advertiserDomains: Array<string>,
    mediaType: string
  },
  netRevenue?: boolean,
  ad: string,
  ttl: number
};

declare type unrulyResponse = {
  bids: Array<resBid>
};

class _unruly extends bidbase {
  _bidRequest: unrulyBidRequestCache;

  _config: unrulyConfig;

  _DefConfig: unrulyConfig;

  _syncURLs: Array<string>;

  constructor(name: string = 'unruly') {
    super(name);
    this._DefConfig = extend(true, {}, this._DefConfig, {
      bidUrl: ''
    });
    this._isSupported = isJSONSupported() && isFetchSupported();
    this._config = extend(true, {}, this._DefConfig);
    this._mandatoryConfigs = [];
    this._bidRequest = {
      impid: {}
    };
  }

  /**
   * - Checks partner specific config for unruly partner
   *
   * @typedef {object} initStatus
   * @returns {initStatus} - Init status
   * @access private
   */
  _configCheck(): initStatus {
    // unruly expects siteId as number
    const chk = Object.keys(this._config.posTokenMap).every((pos: string) => {
      const siteId = this._config.posTokenMap[pos]['siteId'] || 0;
      if (!isNumber(siteId) || siteId === 0) {
        logError(`${this._name} config error: missing mandatory data`);
        return false;
      }
      return true;
    });
    return chk ? super._configCheck() : INIT_STATUS.F_FAILED;
  }

  _getDeviceExt(): deviceExtInfo {
    const [vpw, vph] = getViewportDimensions();
    return {
      ext: {
        vpw,
        vph
      }
    };
  }

  _getDevice(): deviceInfo {
    return {
      device: {
        w: window.screen.width,
        h: window.screen.height,
        dnt: isDNT() ? 1 : 0,
        ua: BrowserUtil.getUserAgent(),

        language: navigator.language || '',
        ...this._getDeviceExt()
      }
    };
  }

  _getRegs(): regsInfo {
    return {
      regs: {
        ext: {
          ...this._getPrivacyString()
        }
      }
    };
  }

  _getSite(): siteInfo {
    const domain = window.location.hostname;
    return {
      site: {
        domain,
        ext: {
          data: { documentLang: navigator.language || '' }
        },
        page: window.location.href,
        publisher: {
          domain
        },
        content: {
          language: navigator.language || ''
        }
      }
    };
  }

  _getOrtb2(): ortb2Info {
    const { user: { eids = [] } = {} } = getRTBUserEids();
    const schain = this.getSupplyChain();
    return {
      ...this._getDevice(),
      ...this._getRegs(),
      ...this._getSite(),
      user: {
        ext: {
          eids
        }
      },
      ...(schain &&
        !isEmptyObject(schain) && {
          source: {
            ext: {
              schain
            }
          }
        })
    };
  }

  _getOrtb2Imp(pos: string): ortb2ImpInfo {
    return {
      ext: {
        data: {
          adserver: {
            adslot: adconfig.getPosSettings(pos).adUnitPath || '',
            name: 'gam'
          }
        },
        gpid: adconfig.getGPID(pos)
      }
    };
  }

  _getRefererInfo(): refererInfo {
    const { href = '' } = window.location;
    return {
      domain: window.location.hostname,
      legacy: {
        referer: href
      },
      location: href,

      page: href,
      topmostLocation: href
    };
  }

  _getBidParamsforpos(pos: string, bidderRequestId: string): bidInfo {
    const { siteId } = this._config.posTokenMap[pos];
    const { sizes = [] } = adconfig.getPosSettings(pos);
    const { cID } = containerHelper.getAdContainerInfo(pos, true);
    const randid = getRandom();
    const ortb2Imp = this._getOrtb2Imp(pos);
    const ortb2 = this._getOrtb2();

    const bidObject = {
      adUnitCode: cID,
      adUnitId: generateUUID(),
      auctionId: getRandom(),
      auctionsCount: 1,
      sizes,
      bidId: randid,
      bidderRequestId,
      bidderRequestsCount: 1,
      bidderWinsCount: 0,
      bidRequestsCount: 1,
      bidder: this._name,
      mediaTypes: {
        banner: {
          floor: 0,
          sizes
        }
      },
      ortb2,
      ortb2Imp,
      params: {
        siteId
      },
      transactionId: getRandom(),
      deferBilling: false,
      src: 'client'
    };
    this._bidRequest.impid[randid] = pos;
    return bidObject;
  }

  /**
   * Build request params
   *
   * @param {Array<object>} slots - Ad position
   * @returns {object} - Request URL info for given positions
   * @access private
   */
  _buildBidderReq(posList: Array<string>): unrulyReqBody {
    // build params for bid request
    const bidderRequestId = getRandom();
    const bids = posList.map(pos =>
      this._getBidParamsforpos(pos, bidderRequestId)
    );
    const ortb2 = this._getOrtb2();
    const refererInfo = this._getRefererInfo();

    return {
      bidderRequest: {
        auctionStart: Date.now(),
        auctionId: getRandom(),
        invalidBidsCount: 0,
        bids,
        ortb2,
        refererInfo,
        start: Date.now(),
        timeout: this._config.timeout,

        uspConsent: this._getPrivacyString().us_privacy
      }
    };
  }

  /**
   * Process bid response. Response format:
   *
   * @param {Array<string>} posList - HB position list
   * @param {object} response - Response from server
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {object.<string, hbPosData>} - Return data
   */
  _processBidResponse(
    posList: Array<string>,
    response: unrulyResponse,
    ret: hbPartnerData
  ): hbPartnerData {
    const { bids = [] } = response || {};
    if (!bids.length) {
      return ret;
    }
    response.bids.forEach(bid => {
      if (bid && bid.requestId && bid.ad) {
        const { requestId, ad, cpm = 0 } = bid;
        const price = cpm.toString();
        this._handleBidPosData(
          ret,
          this._bidRequest.impid[requestId],
          price,
          requestId,
          ad
        );
      }
    });
    return ret;
  }

  /**
   * Builds and sends bid requests
   *
   * @param {Array<string>} posList - Ad positions
   * @returns {Promise} - Returns promise
   * @access private
   */
  _getBids(posList: Array<string>): Promise<unrulyResponse> {
    const reqParams = this._buildBidderReq(posList);
    const { bidUrl } = this._config;
    const opt = {
      data: {},
      timeout: this._config.timeout,
      logPrefix: `${this.getName()} - ${posList.join(',')}`
    };

    const req = {
      method: 'POST',
      body: JSON.stringify(reqParams),
      credentials: 'include'
    };
    return fetchJSON(bidUrl, opt, req);
  }

  /**
   * get bids for given positions
   *
   * @param {Array<string>} posList - HB Position list
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {Promise<object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    this._bidResponses = {};
    this._bidRequest.impid = {};
    return this._getBids(posList).then(
      response => this._processBidResponse(posList, response, ret),
      error => {
        logError(`${this._name} Failed to get bids: ${error || ''}`);
        return ret;
      }
    );
  }
}

const unruly: _unruly = new _unruly();
export default unruly;
